import { useEffect, useId, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/api/client";
import { PROJECT_UNSET_TOKEN } from "@/constants";

type SeriesPoint = { ts: string; hour: string; requests: number; errors: number };

type ByProject = {
  project: string;
  key_count: number;
  requests_24h: number;
  errors_24h: number;
  error_rate_24h: number;
  avg_latency_ms: number;
  last_seen: string | null;
  cost_24h_usd: number;
  cost_mtd_usd: number;
  series: SeriesPoint[];
};

type StatsLite = {
  by_project: ByProject[];
  totals: { real_keys: number; proxy_keys_active: number };
  last_24h: { requests: number; errors: number; error_rate: number; avg_latency_ms: number };
};

const REFRESH_MS = 5_000;

function timeSince(iso: string | null): { text: string; warm: boolean } {
  if (!iso) return { text: "—", warm: false };
  const sec = (Date.now() - new Date(iso).getTime()) / 1000;
  if (sec < 60) return { text: `${Math.round(sec)}s ago`, warm: true };
  if (sec < 3600) return { text: `${Math.round(sec / 60)}m ago`, warm: sec < 600 };
  if (sec < 86400) return { text: `${Math.round(sec / 3600)}h ago`, warm: false };
  return { text: `${Math.round(sec / 86400)}d ago`, warm: false };
}

function fmtClock(d: Date): string {
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false });
}

export function ProjectsMonitor() {
  const [stats, setStats] = useState<StatsLite | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [updatedAt, setUpdatedAt] = useState<number | null>(null);
  const [tick, setTick] = useState(0);
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    let cancelled = false;
    let timer: number | undefined;

    const fetchNow = async () => {
      try {
        const s = await api<StatsLite>("/api/admin/stats");
        if (!cancelled) {
          setStats(s);
          setUpdatedAt(Date.now());
          setErr(null);
        }
      } catch (e: any) {
        if (!cancelled) setErr(e?.message ?? "load failed");
      }
    };

    const schedule = () => {
      timer = window.setTimeout(async () => {
        if (document.visibilityState === "visible") await fetchNow();
        if (!cancelled) schedule();
      }, REFRESH_MS);
    };

    fetchNow();
    schedule();
    const onVis = () => {
      if (document.visibilityState === "visible") fetchNow();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  useEffect(() => {
    const t = window.setInterval(() => {
      setTick((n) => n + 1);
      setNow(new Date());
    }, 1000);
    return () => window.clearInterval(t);
  }, []);

  const ago = updatedAt === null ? null : Math.max(0, Math.round((Date.now() - updatedAt) / 1000));
  const _ = tick;

  const projects = stats?.by_project ?? [];

  const fullscreenSupported =
    typeof document !== "undefined" && (document as Document).fullscreenEnabled === true;
  const goFullscreen = () => {
    if (!fullscreenSupported) return;
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen?.().catch(() => {
        /* swallow — user may have denied permission, no point logging */
      });
    } else {
      document.exitFullscreen?.().catch(() => {});
    }
  };

  const aggregate = useMemo(() => {
    if (!projects.length) return null;
    const live = projects.filter((p) => {
      if (!p.last_seen) return false;
      return Date.now() - new Date(p.last_seen).getTime() < 5 * 60_000;
    }).length;
    return { total: projects.length, live };
  }, [projects]);

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-4xl display tracking-tight">Project Monitor</h1>
          <p className="text-sm text-slate-400">
            All consumer projects routing through Gatekeeper. Auto-refreshes every {REFRESH_MS / 1000}s.
          </p>
        </div>
        <div className="flex items-center gap-4 text-right">
          <div>
            <div className="text-3xl mono tracking-wider text-slate-100">{fmtClock(now)}</div>
            <div className="text-xs text-slate-500">{now.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" })}</div>
          </div>
          {fullscreenSupported && (
            <button
              onClick={goFullscreen}
              className="text-xs px-3 py-2 rounded border border-white/10 hover:bg-white/5 text-slate-300"
              title="Toggle fullscreen (F11)"
            >
              ⛶ Fullscreen
            </button>
          )}
        </div>
      </header>

      <div className="flex items-center justify-between text-xs text-slate-500 border-y border-white/5 py-3">
        <div className="flex items-center gap-6">
          {stats && (
            <>
              <span>
                <span className="text-slate-200 font-medium">{stats.totals.proxy_keys_active}</span> active keys across{" "}
                <span className="text-slate-200 font-medium">{aggregate?.total ?? 0}</span> projects
              </span>
              <span>
                <span className="text-emerald-300">●</span>{" "}
                <span className="text-slate-200 font-medium">{aggregate?.live ?? 0}</span> live (request in last 5m)
              </span>
              <span>
                24h:{" "}
                <span className="text-slate-200 font-medium">{stats.last_24h.requests.toLocaleString()}</span> req,{" "}
                <span className={stats.last_24h.errors > 0 ? "text-rose-300" : "text-slate-300"}>
                  {stats.last_24h.errors} err
                </span>
                {stats.last_24h.requests > 0 && (
                  <> · {Math.round(stats.last_24h.avg_latency_ms)}ms avg</>
                )}
              </span>
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span
            className={`inline-block w-2 h-2 rounded-full ${
              ago !== null && ago < 8 ? "bg-emerald-500 animate-pulse" : "bg-amber-500"
            }`}
          />
          {ago === null ? "loading…" : ago < 5 ? "just refreshed" : `${ago}s since refresh`}
        </div>
      </div>

      {err && (
        <div className="card text-rose-300 text-sm">
          {err}
          {err.toLowerCase().includes("authenticat") && (
            <Link to="/login" className="ml-2 text-accent-500 underline">
              re-login
            </Link>
          )}
        </div>
      )}
      {!stats && !err && <div className="card text-slate-500 text-sm">Loading…</div>}

      {projects.length === 0 && stats && (
        <div className="card text-slate-500 text-sm">
          No proxy keys issued yet. Once a consumer project is wired, its tile will appear here.
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-5">
        {projects.map((p) => (
          <ProjectTile
            key={p.project}
            p={p}
            onRenamed={() => {
              // Bump tick to force a refetch on next visibility/refresh; also fetch now.
              api<StatsLite>("/api/admin/stats")
                .then((s) => {
                  setStats(s);
                  setUpdatedAt(Date.now());
                })
                .catch(() => {});
            }}
          />
        ))}
      </div>
    </div>
  );
}

function ProjectSpark({ data, live }: { data: SeriesPoint[]; live: boolean }) {
  if (!data || data.length === 0) {
    return <div className="h-10 rounded bg-white/[0.02] border border-white/5" />;
  }
  const W = 280;
  const H = 44;
  const pad = 3;
  const innerW = W - pad * 2;
  const innerH = H - pad * 2;
  const max = Math.max(1, ...data.map((d) => d.requests));
  const stepX = innerW / Math.max(1, data.length - 1);
  const pts = data.map((d, i) => ({
    x: pad + i * stepX,
    y: pad + innerH - (d.requests / max) * innerH,
    ...d,
  }));
  const lineD = pts.map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
  const areaD = `${lineD} L${pad + innerW},${pad + innerH} L${pad},${pad + innerH} Z`;
  const stroke = live ? "rgb(110 231 183)" : "rgb(148 163 184)"; // emerald-300 / slate-400
  const fillTop = live ? "rgba(16,185,129,0.35)" : "rgba(148,163,184,0.18)";
  const fillBot = live ? "rgba(16,185,129,0)" : "rgba(148,163,184,0)";
  // Thin baseline grid every 6 hours so eye can read time roughly
  const gridXs = [6, 12, 18].map((i) => pad + i * stepX);
  // Stable per-mount ID so the 5s auto-refresh doesn't append orphan
  // <linearGradient> nodes on every render (audit finding #8).
  const gradientId = `pspark-${useId().replace(/:/g, "")}`;
  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      width="100%"
      height={H}
      preserveAspectRatio="none"
      className="block"
      role="img"
      aria-label="24-hour request sparkline"
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={fillTop} />
          <stop offset="100%" stopColor={fillBot} />
        </linearGradient>
      </defs>
      {gridXs.map((x) => (
        <line key={x} x1={x} y1={pad} x2={x} y2={pad + innerH} stroke="rgba(255,255,255,0.04)" strokeWidth={1} />
      ))}
      <path d={areaD} fill={`url(#${gradientId})`} />
      <path d={lineD} fill="none" stroke={stroke} strokeWidth={1.5} strokeLinejoin="round" />
      {pts.map((p) => (
        <g key={p.ts}>
          {p.errors > 0 && (
            <circle cx={p.x} cy={p.y} r={3} fill="none" stroke="rgb(244 63 94)" strokeWidth={1.1} opacity={0.85} />
          )}
          <title>
            {p.hour}: {p.requests} req{p.errors > 0 ? `, ${p.errors} err` : ""}
          </title>
        </g>
      ))}
    </svg>
  );
}

function ProjectTile({ p, onRenamed }: { p: ByProject; onRenamed: () => void }) {
  const seen = timeSince(p.last_seen);
  const errPct = p.error_rate_24h * 100;
  const live = seen.warm && p.last_seen !== null;
  const errTone =
    errPct > 5
      ? "text-rose-300 border-rose-500/30 bg-rose-500/5"
      : errPct > 0
      ? "text-amber-300 border-amber-500/30 bg-amber-500/5"
      : "text-emerald-300 border-emerald-500/20 bg-emerald-500/5";
  const isPlaceholder = p.project === "(no project)";
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(isPlaceholder ? "" : p.project);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const beginEdit = () => {
    setDraft(isPlaceholder ? "" : p.project);
    setErr(null);
    setEditing(true);
  };
  const cancel = () => {
    setEditing(false);
    setErr(null);
  };
  const save = async () => {
    const next = draft.trim();
    const current = isPlaceholder ? null : p.project;
    if (next === (current ?? "")) {
      setEditing(false);
      return;
    }
    if (next.length === 0) {
      setErr("name cannot be empty");
      return;
    }
    if (next.length > 64) {
      setErr("max 64 characters");
      return;
    }
    setSaving(true);
    try {
      await api("/api/admin/projects/rename", {
        method: "POST",
        json: { old: current, new: next },
      });
      setEditing(false);
      onRenamed();
    } catch (e: any) {
      setErr(e?.message ?? "rename failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className={`relative rounded-xl border p-5 transition-colors ${
        live ? "border-accent-500/30 bg-accent-500/[0.03]" : "border-white/10 bg-white/[0.02]"
      }`}
    >
      <div className="flex items-center justify-between mb-3 gap-2">
        {editing ? (
          <div className="flex-1 flex items-center gap-1">
            <input
              autoFocus
              value={draft}
              onChange={(e) => {
                setDraft(e.target.value);
                if (err) setErr(null);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") save();
                else if (e.key === "Escape") cancel();
              }}
              disabled={saving}
              maxLength={64}
              placeholder={isPlaceholder ? "name this project…" : ""}
              className="flex-1 min-w-0 bg-slate-900/60 border border-accent-500/40 rounded px-2 py-1 text-xl display text-slate-100 outline-none focus:border-accent-500"
            />
            <button
              onClick={save}
              disabled={saving}
              title="Save (Enter)"
              className="text-xs px-2 py-1 rounded bg-accent-500/20 border border-accent-500/40 text-accent-200 hover:bg-accent-500/30 disabled:opacity-50"
            >
              {saving ? "…" : "Save"}
            </button>
            <button
              onClick={cancel}
              disabled={saving}
              title="Cancel (Esc)"
              className="text-xs px-2 py-1 rounded border border-white/10 text-slate-400 hover:bg-white/5 disabled:opacity-50"
            >
              ✕
            </button>
          </div>
        ) : (
          <div className="flex-1 min-w-0 group flex items-center gap-1">
            <button
              type="button"
              onClick={beginEdit}
              title={`Rename ${p.project} (${p.key_count} key${p.key_count === 1 ? "" : "s"})`}
              className={`min-w-0 flex-1 text-left flex items-center gap-2 rounded px-1 -mx-1 hover:bg-white/[0.04] ${
                isPlaceholder ? "text-slate-500 italic" : "text-slate-100"
              }`}
            >
              <h2 className="display text-xl leading-tight break-words" title={p.project}>
                {p.project}
              </h2>
              <span
                className="text-[10px] text-slate-500 opacity-0 group-hover:opacity-100 transition-opacity"
                aria-hidden="true"
              >
                ✎
              </span>
            </button>
            <Link
              to={`/projects/${encodeURIComponent(isPlaceholder ? PROJECT_UNSET_TOKEN : p.project)}`}
              title="Open project detail"
              className="shrink-0 text-xs px-2 py-1 rounded border border-white/10 text-slate-400 hover:bg-white/5 hover:text-slate-100 hover:border-white/20 transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              Open <span aria-hidden="true">→</span>
            </Link>
          </div>
        )}
        <span
          className={`shrink-0 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${
            live
              ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
              : "bg-white/5 text-slate-500 border border-white/10"
          }`}
        >
          {live ? "● live" : "idle"}
        </span>
      </div>
      {err && (
        <div className="mb-2 text-[11px] text-rose-300 bg-rose-500/5 border border-rose-500/20 rounded px-2 py-1">
          {err}
        </div>
      )}

      <div className="space-y-3">
        <div>
          <div className="flex items-baseline justify-between">
            <div className="text-[11px] uppercase tracking-wider text-slate-500">Requests · 24h</div>
            <div className="text-[10px] text-slate-600 mono">hourly</div>
          </div>
          <div className="text-4xl mono font-semibold text-slate-100 mt-0.5">
            {p.requests_24h.toLocaleString()}
          </div>
          <div className="mt-2">
            <ProjectSpark data={p.series} live={live} />
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2 text-xs">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Errors</div>
            <div className={`mt-0.5 font-medium px-2 py-1 rounded border ${errTone}`}>
              {p.errors_24h}
              {errPct > 0 && <span className="opacity-70"> · {errPct.toFixed(1)}%</span>}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Latency</div>
            <div className="mt-0.5 mono text-slate-300 px-2 py-1 rounded border border-white/10 bg-white/[0.02]">
              {p.avg_latency_ms > 0 ? `${Math.round(p.avg_latency_ms)}ms` : "—"}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Keys</div>
            <div className="mt-0.5 mono text-slate-300 px-2 py-1 rounded border border-white/10 bg-white/[0.02]">
              {p.key_count}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 text-xs pt-1">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Cost · 24h</div>
            <div className="mt-0.5 mono text-slate-300 px-2 py-1 rounded border border-white/10 bg-white/[0.02]">
              {p.cost_24h_usd > 0 ? `$${p.cost_24h_usd.toFixed(4)}` : "—"}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">Cost · month</div>
            <div className="mt-0.5 mono text-slate-300 px-2 py-1 rounded border border-white/10 bg-white/[0.02]">
              {p.cost_mtd_usd > 0 ? `$${p.cost_mtd_usd.toFixed(2)}` : "—"}
            </div>
          </div>
        </div>

        <div className="text-[11px] text-slate-500 flex items-center justify-between pt-1 border-t border-white/5">
          <span>last seen</span>
          <span className={live ? "text-emerald-300" : "text-slate-400"}>{seen.text}</span>
        </div>
      </div>
    </div>
  );
}
